import { useRef } from "react";
import { AlertTriangle, Bold, Heading2, Link2, List, ListOrdered, Table as TableIcon, Zap } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { SECTION_LABELS } from "@/lib/policyLabels";

const BOLD_RE = /(\*\*[^*]+?\*\*)/g;
const LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/g;

/** Only https:, mailto: and internal "/" links render as anchors; anything else is plain text. */
function safeHref(url) {
  const u = String(url || "").trim();
  if (u.startsWith("/")) return u;
  if (/^(https:\/\/|mailto:)/i.test(u)) return u;
  return null;
}

function renderLinks(seg, k) {
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(seg))) {
    if (m.index > last) out.push(<span key={`${k}-t${i}`}>{seg.slice(last, m.index)}</span>);
    const href = safeHref(m[2]);
    if (href) {
      const external = /^https:/i.test(href);
      out.push(
        <a key={`${k}-a${i}`} href={href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="text-primary underline underline-offset-2 hover:text-cyan-700">{m[1]}</a>
      );
    } else {
      out.push(<span key={`${k}-b${i}`}>{m[1]}</span>);
    }
    last = m.index + m[0].length;
    i++;
  }
  if (last < seg.length) out.push(<span key={`${k}-e`}>{seg.slice(last)}</span>);
  return out;
}

function renderInline(text, k) {
  return String(text).split(BOLD_RE).map((seg, j) =>
    seg.startsWith("**") && seg.endsWith("**")
      ? <strong key={`${k}-${j}`}>{renderLinks(seg.slice(2, -2), `${k}-${j}`)}</strong>
      : renderLinks(seg, `${k}-${j}`)
  );
}

function renderTableBlock(rows, k) {
  const parseRow = (r) => r.replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
  const isSep = (r) => /^\|[\s:|-]+\|?$/.test(r);
  let header = null;
  let body = rows;
  if (rows.length > 1 && isSep(rows[1])) {
    header = parseRow(rows[0]);
    body = rows.slice(2);
  }
  const bodyRows = body.filter((r) => !isSep(r)).map(parseRow);
  return (
    <div key={k} className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        {header && (
          <thead>
            <tr className="bg-slate-50">
              {header.map((c, i) => (
                <th key={i} className="border-b border-border px-3 py-2 text-left font-semibold text-slate-800">{renderInline(c, `${k}-h${i}`)}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((r, ri) => (
            <tr key={ri} className={ri % 2 ? "bg-slate-50/50" : "bg-white"}>
              {r.map((c, ci) => (
                <td key={ci} className="border-b border-border/60 px-3 py-2 text-slate-700 align-top">{renderInline(c, `${k}-${ri}-${ci}`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Safe markdown-lite renderer for policy text. Everything renders as React text nodes —
 *  no HTML is ever injected, so stored content cannot execute scripts.
 *  Supports: **bold**, [links](https://… or /internal), "## " / "### " headings,
 *  "- " bullets, "1." numbered steps, "!warn " warning boxes, "!action " quick-action
 *  boxes, and pipe tables ("| a | b |" with an optional "| --- | --- |" header separator). */
export function renderPolicyText(text) {
  if (!text) return null;
  const lines = String(text).split("\n");
  const blocks = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }
    if (line.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(lines[i].trim());
        i++;
      }
      blocks.push(renderTableBlock(rows, k++));
      continue;
    }
    if (line.startsWith("## ")) {
      blocks.push(<h4 key={k++} className="font-heading text-sm font-bold text-slate-900 mt-3">{renderInline(line.slice(3), k)}</h4>);
      i++; continue;
    }
    if (line.startsWith("### ")) {
      blocks.push(<h5 key={k++} className="text-sm font-semibold text-slate-800 mt-2">{renderInline(line.slice(4), k)}</h5>);
      i++; continue;
    }
    if (/^!(warn|warning)\s+/i.test(line)) {
      blocks.push(
        <div key={k++} className="flex gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="policy-warning-box">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900 leading-relaxed">{renderInline(line.replace(/^!(warn|warning)\s+/i, ""), k)}</p>
        </div>
      );
      i++; continue;
    }
    if (/^!action\s+/i.test(line)) {
      blocks.push(
        <div key={k++} className="flex gap-2.5 rounded-md border border-cyan-300 bg-cyan-50 px-3 py-2.5" data-testid="policy-action-box">
          <Zap className="h-4 w-4 text-cyan-700 shrink-0 mt-0.5" />
          <p className="text-sm text-cyan-950 leading-relaxed">{renderInline(line.replace(/^!action\s+/i, ""), k)}</p>
        </div>
      );
      i++; continue;
    }
    if (line.startsWith("- ")) {
      blocks.push(
        <div key={k++} className="flex gap-2 pl-1">
          <span className="text-primary">•</span>
          <p className="text-sm text-slate-700 leading-relaxed">{renderInline(line.slice(2), k)}</p>
        </div>
      );
      i++; continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const n = line.match(/^(\d+)\.\s/)[1];
      blocks.push(
        <div key={k++} className="flex gap-2 pl-1">
          <span className="text-primary font-medium shrink-0">{n}.</span>
          <p className="text-sm text-slate-700 leading-relaxed">{renderInline(line.replace(/^\d+\.\s/, ""), k)}</p>
        </div>
      );
      i++; continue;
    }
    blocks.push(<p key={k++} className="text-sm text-slate-700 leading-relaxed">{renderInline(line, k)}</p>);
    i++;
  }
  return blocks;
}

export default function PolicySections({ sections }) {
  if (!sections) return null;
  const keys = [
    ...Object.keys(SECTION_LABELS).filter((k) => sections[k]),
    ...Object.keys(sections).filter((k) => !SECTION_LABELS[k]),
  ];
  return (
    <div className="space-y-5" data-testid="policy-sections">
      {keys.map((key) => (
        <section key={key} data-testid={`policy-section-${key}`}>
          <h3 className="font-heading text-base font-semibold text-slate-900 mb-1.5">
            {SECTION_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          </h3>
          <div className="space-y-1.5">{renderPolicyText(sections[key])}</div>
        </section>
      ))}
    </div>
  );
}

/** Plain-text editor with a formatting toolbar. Content is stored as safe markdown-lite
 *  text; no HTML is ever stored or rendered, which prevents XSS by construction. */
export function PolicyTextarea({ value, onChange, rows = 3, testid, placeholder }) {
  const ref = useRef(null);
  const val = value || "";
  const setVal = (v) => onChange({ target: { value: v } });
  const restore = (start, end) => requestAnimationFrame(() => {
    const el = ref.current;
    if (el) { el.focus(); el.setSelectionRange(start, end); }
  });

  const wrap = (before, after, ph) => {
    const el = ref.current;
    const s = el ? el.selectionStart : val.length;
    const e = el ? el.selectionEnd : val.length;
    const sel = val.slice(s, e) || ph;
    setVal(val.slice(0, s) + before + sel + after + val.slice(e));
    restore(s + before.length, s + before.length + sel.length);
  };

  const linePrefix = (prefix) => {
    const el = ref.current;
    const s = el ? el.selectionStart : val.length;
    const start = val.lastIndexOf("\n", s - 1) + 1;
    setVal(val.slice(0, start) + prefix + val.slice(start));
    restore(s + prefix.length, s + prefix.length);
  };

  const insertBlock = (block) => {
    const el = ref.current;
    const s = el ? el.selectionStart : val.length;
    setVal(val.slice(0, s) + block + val.slice(s));
    restore(s + block.length, s + block.length);
  };

  const tools = [
    { icon: Bold, label: "Bold", tid: "bold", fn: () => wrap("**", "**", "bold text") },
    { icon: Heading2, label: "Heading", tid: "heading", fn: () => linePrefix("## ") },
    { icon: List, label: "Bullet list", tid: "bullet", fn: () => linePrefix("- ") },
    { icon: ListOrdered, label: "Numbered step", tid: "numbered", fn: () => linePrefix("1. ") },
    { icon: Link2, label: "Link (https:// or internal /page)", tid: "link", fn: () => wrap("[", "](https://)", "link text") },
    { icon: AlertTriangle, label: "Warning box", tid: "warning", fn: () => linePrefix("!warn ") },
    { icon: Zap, label: "Quick action box", tid: "action", fn: () => linePrefix("!action ") },
    { icon: TableIcon, label: "Table", tid: "table", fn: () => insertBlock("\n| Column | Column |\n| --- | --- |\n| | |\n") },
  ];

  return (
    <div className="rounded-md border border-input focus-within:ring-1 focus-within:ring-ring">
      <div className="flex flex-wrap gap-0.5 border-b border-border bg-slate-50 px-1.5 py-1 rounded-t-md" data-testid={`${testid}-toolbar`}>
        {tools.map(({ icon: Icon, label, tid, fn }) => (
          <button key={tid} type="button" title={label} aria-label={label} data-testid={`${testid}-tool-${tid}`} onClick={fn}
            className="p-1.5 rounded text-slate-600 hover:bg-white hover:text-primary transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center">
            <Icon className="h-3.5 w-3.5" />
          </button>
        ))}
      </div>
      <Textarea ref={ref} rows={rows} data-testid={testid} value={val} onChange={onChange} placeholder={placeholder}
        className="border-0 focus-visible:ring-0 rounded-t-none" />
    </div>
  );
}
